<script setup lang="ts">
import { close, qrCode } from "ionicons/icons";
import { useContextStore } from "@/stores/context";
import { BarcodeScanner } from "@capacitor-community/barcode-scanner";
import { QrcodeStream } from "vue-qrcode-reader";
import {
  IonContent,
  IonPage,
  IonFab,
  IonProgressBar,
  IonFabButton,
  IonModal,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
} from "@ionic/vue";
import { reactive, computed, ref } from "vue";
import { getVisitors } from "@/api/visitors";
import {
  getSessions,
  sessionRegistrationCreate,
  sessionEntranceCreate,
  getSessionRegistrations,
} from "@/api/sessions";
import UserCard from "@/views/_partials/UserCard.vue";
import { IVisitor } from "@/models/visitor";
import router from "@/router";
import { useAppStore } from "@/stores/app";
import { useCheckinStore } from "@/stores/check-in";
import { useToasters } from "@/composition/toasters";
import { ISession, ISessionRegistration } from "@/models/session";
import { handleError } from "@/views";
import EventSidebar from "@/views/_partials/EventSidebar.vue";
import { useWindowWidth } from "@/composition/window";
import { event as gtag_event } from "vue-gtag";

const { isMobile } = useWindowWidth();

const contextStore = useContextStore();
const appStore = useAppStore();
const checkInStore = useCheckinStore();
const toasters = useToasters();

const modal = ref(null as any);

const state = reactive({
  scanActive: false,
  loading: false,
  checkInRegistration: undefined as ISessionRegistration | undefined,
  webScannerOpened: false,
  currentVisitorId: 0 as number | undefined,
  toastButtons: [
    {
      text: "SEE",
      role: "cancel",
      handler: () => {
        router.push(
          `/event/${contextStore.event.id}/visitor/${state.currentVisitorId}`
        );
      },
    },
  ],
});

const visitors = computed(() =>
  checkInStore.visitors.filter(
    (visitor) => visitor.event === contextStore.event.id
  )
);

const checkPermission = async () => {
  const status = await BarcodeScanner.checkPermission({ force: true });
  if (status.granted) {
    return true;
  } else {
    const new_permission = await BarcodeScanner.checkPermission({ force: true });
    return new_permission.granted;
  }
};

const onDecode = (decodedString: any) => {
  checkInVisitor(decodedString.rawValue);
  stopWebScanner();
};

const startWebScanner = () => {
  state.webScannerOpened = true;
};

const stopWebScanner = () => {
  state.webScannerOpened = false;
};

const startScanner = async () => {
  const allowed = await checkPermission();

  if (allowed) {
    document.querySelector('body')?.classList.add("scanner-active");
    BarcodeScanner.hideBackground();
    state.scanActive = true;

    const result = await BarcodeScanner.startScan();

    if (result.hasContent) {
      if (result.content) {
        checkInVisitor(result.content);
      }
    } else {
      toasters.addError("Failed to scan QR code");
    }
  } else {
    toasters.addError("Failed to obtain permissions to launch camera");
  }
};

const stopScanner = () => {
  document.querySelector('body')?.classList.remove("scanner-active");
  BarcodeScanner.stopScan();
  state.scanActive = false;
};

const addEnteredVisitor = (visitor: IVisitor) => {
  checkInStore.add(visitor);

  toasters.addSuccess(`Visitor ${visitor.name} scanned successfuly`);

  if (contextStore.event.checked_in_visitors === 1) {
    gtag_event("first_visitor_scanned", {
      category: "check-in app",
      label: "First visitor scanned",
    });
  }

  contextStore.addEventCheckin();
  state.loading = false;

  if (appStore.settings.checkin_mode === "slow") {
    router.push(`/event/${contextStore.event.id}/visitor/${visitor.id}`);
  } else {
    startScanner();
  }
};

const makeVisitorEntered = (visitor: IVisitor, session: ISession) => {
  sessionEntranceCreate({
    attending: visitor.id || 0,
    session: session.id,
  })
    .then(() => {
      addEnteredVisitor(visitor);
    })
    .catch((err) => {
      state.loading = false;

      if (Array.isArray(err.response.data)) {
        toasters.addError(err.response.data[0]);
        startScanner();
      } else if (Array.isArray(err.response.data._errors)) {
        toasters.addError(err.response.data._errors[0].message);
      } else {
        handleError(err);
      }
    });
};

const checkInVisitor = async (register_code: string) => {
  state.loading = true;
  getVisitors({
    event: contextStore.event.id,
    register_code: register_code,
  })
    .then(async (response) => {
      const visitor = response.data.results[0];

      if (visitor) {
        const responseSessions = await getSessions({
          event: contextStore.event.id,
          search: "check-in",
        });

        state.currentVisitorId = visitor.id;

        const session = responseSessions.data?.results[0];

        getSessionRegistrations({
          event: contextStore.event.id,
          visitor: visitor.id,
          session: session.id,
        })
          .then((response) => {
            state.checkInRegistration = response.data.results[0];
            // Check if this user exists and if it has entered status
            if (
              state.checkInRegistration &&
              state.checkInRegistration.status === 2
            ) {
              toasters.addWarning(
                `The guest ${visitor.name} is already inside.`,
                state.toastButtons
              );
              stopScanner();
              startScanner();
            } else {
              if (session && visitor.id) {
                sessionRegistrationCreate({
                  attending: visitor.id,
                  session: session.id,
                })
                  .then(() => {
                    makeVisitorEntered(visitor, session);
                  })
                  .catch((err) => {
                    state.loading = false;
                    makeVisitorEntered(visitor, session);
                    toasters.addError(err.response.data._errors[0].message);
                  });
              } else {
                state.loading = false;
                toasters.addError("Check-in session was not found");
                startScanner();
              }
            }
          })
          .catch(() => {
            state.loading = false;
          });
      }
    })
    .catch((err) => {
      if (Array.isArray(err.response.data)) {
        toasters.addError(err.response.data[0]);
        startScanner();
      } else if (Array.isArray(err.response.data._errors)) {
        toasters.addError(err.response.data._errors[0].message);
      } else {
        handleError(err);
      }
    });
};

const cardConfig = (visitor: IVisitor) => {
  return {
    name: visitor.name,
    detail: visitor.email,
    picture: visitor.profile_picture,
  };
};

router.beforeEach(() => {
  if (state.scanActive) {
    stopScanner();
  }
});
</script>

<template>
  <ion-page>
    <ion-content>
      <div class="flex-desktop">
        <event-sidebar v-if="!isMobile" :event="contextStore.event" />
        <div class="colored-bg full-width">
          <div class="container" v-if="!state.scanActive">
            <div
              class="flex-desktop"
              style="justify-content: space-between; align-items: center"
            >
              <div>
                <h1>Check-ins</h1>
                <p>List of your latest check-ins</p>
              </div>
              <div v-if="!isMobile">
                <ion-fab-button
                  @click="startWebScanner()"
                  color="success"
                  v-if="!state.scanActive"
                >
                  <ion-icon :icon="qrCode"></ion-icon>
                </ion-fab-button>
                <ion-fab-button
                  @click="stopScanner()"
                  v-if="state.scanActive"
                  color="light"
                >
                  <ion-icon :icon="close"></ion-icon>
                </ion-fab-button>
              </div>
            </div>
            <!-- <ion-item
              class="ion-margin-top ion-margin-bottom"
              v-if="checkInStore.visitors.length > 5"
            >
              <ion-searchbar
                placeholder="Search visitors"
                @change="search()"
                v-model="form.search"
              ></ion-searchbar>
            </ion-item> -->
            <UserCard
              :user="cardConfig(visitor)"
              :route="`/event/${contextStore.event.id}/visitor/${visitor.id}`"
              v-for="(visitor, index) in visitors"
              :key="`visitor-${index}`"
            />
            <figure class="image" v-if="visitors.length === 0">
              <img src="@/assets/undraw_no_data.svg" alt="" />
              <hr />
              <p class="ion-text-center">
                You haven't<br />
                scanned anyone yet!
              </p>
            </figure>
          </div>
          <div class="scan-wrapper" v-else>
            <div class="container">
              <section>
                <ion-grid>
                  <ion-row>
                    <ion-col size="6">
                      <p>Attendance status</p>
                    </ion-col>
                    <ion-col size="2">
                      <small
                        >{{ contextStore.event.checked_in_visitors }} of
                        {{ contextStore.event.registered_visitors }}</small
                      >
                    </ion-col>
                    <ion-col>
                      <ion-progress-bar
                        style="margin-top: 0.6rem"
                        :value="
                          contextStore.event.checked_in_visitors /
                          contextStore.event.registered_visitors
                        "
                      ></ion-progress-bar>
                    </ion-col>
                  </ion-row>
                </ion-grid>
                <template v-if="visitors[0]">
                  <p>Latest scan</p>
                  <UserCard
                    :route="`/event/${contextStore.event.id}/visitor/${visitors[0].id}`"
                    :user="cardConfig(visitors[0])"
                  />
                </template>
              </section>
            </div>
            <div class="scan-box"></div>
          </div>
        </div>
      </div>
    </ion-content>
    <ion-fab v-if="isMobile" vertical="bottom" horizontal="center" slot="fixed">
      <ion-fab-button
        @click="startScanner()"
        color="success"
        v-if="!state.scanActive"
      >
        <ion-icon :icon="qrCode"></ion-icon>
      </ion-fab-button>
      <ion-fab-button
        @click="stopScanner()"
        v-if="state.scanActive"
        color="light"
      >
        <ion-icon :icon="close"></ion-icon>
      </ion-fab-button>
    </ion-fab>
    <ion-modal
      ref="modal"
      :keep-contents-mounted="false"
      :is-open="state.webScannerOpened"
      @did-dismiss="stopWebScanner"
    >
      <div>
        <h1>Scan QR code</h1>
        <qrcode-stream
          v-if="state.webScannerOpened"
          @detect="onDecode"
        ></qrcode-stream>
        <ion-button
          color="danger"
          @click="stopWebScanner()"
          style="position: relative; bottom: 150px; left: 260px"
          >Cancel</ion-button
        >
      </div>
    </ion-modal>
  </ion-page>
</template>
<style scoped lang="scss">
.scan-wrapper {
  display: flex;
  flex-flow: column;
  height: 100%;
  .container {
    flex: 0 1 auto;
    // margin-top: -1.25rem;
    background: var(--ion-color-dark-contrast);
  }
  .scan-box {
    display: block;
    flex: 1 1 auto;
    border: 3rem solid rgba(0, 0, 0, 0.8);
  }
}

.image {
  width: 50vw;
  margin: 4rem auto 0;
  display: block;
}

.colored-bg.full-width {
  overflow-y: auto;
}

ion-modal {
  --width: 630px;
  --height: 610px;
}

@media (min-width: 768px) {
  .image {
    width: 300px;
  }

  .colored-bg.full-width {
    overflow-y: auto;
    height: calc(100vh - 72px);
  }
}
</style>
